import axios from 'axios'
import { config } from '@/shared/config'

export const api = axios.create({
  baseURL: config.apiUrl,
  withCredentials: true,
  // Заголовок X-XSRF-TOKEN axios сам подставляет только с этим флагом:
  // с версии 1.6 для кросс-доменных запросов он выключен по умолчанию, а
  // фронт и API живут на разных поддоменах.
  withXSRFToken: true,
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
})

/** Корень API без версии: /sanctum/csrf-cookie живёт рядом с /api. */
const root = config.apiUrl.replace(/\/api\/v1\/?$/, '')

function hasCsrfCookie(): boolean {
  return document.cookie.split('; ').some((cookie) => cookie.startsWith('XSRF-TOKEN='))
}

/**
 * Получить куку XSRF, если её ещё нет.
 *
 * Нужна каждому изменяющему запросу, а не только входу: Sanctum в режиме SPA
 * включает сессию и проверку CSRF на все запросы с нашего домена — в том
 * числе на создание партии и ход, которые делаются без всякой регистрации.
 */
export async function ensureCsrf(): Promise<void> {
  if (hasCsrfCookie()) return

  await refreshCsrf()
}

/**
 * Забрать куку заново, даже если она есть.
 *
 * Нужно после 419: кука на месте, но токен в ней уже не тот — сессия
 * протухла. Голым axios, а не через api: у того на запросе висит
 * перехватчик, который снова позвал бы сюда.
 */
async function refreshCsrf(): Promise<void> {
  await axios.get(`${root}/sanctum/csrf-cookie`, { withCredentials: true })
}

const MUTATING = ['post', 'put', 'patch', 'delete']

/**
 * Свой номер сокета — его подставляет echo.ts.
 *
 * Через регистрацию, а не импортом: echo.ts сам импортирует этот модуль (его
 * axios авторизует приватные каналы), и обратный импорт замкнул бы круг.
 *
 * Пока сокет не поднялся, геттер возвращает undefined — заголовка тогда нет,
 * и сервер рассылает событие всем, включая нас. Это рабочий запасной путь,
 * а не поломка: лишний запрос вместо потерянного обновления.
 */
let ownSocket: () => string | undefined = () => undefined

export function provideSocketId(getter: () => string | undefined): void {
  ownSocket = getter
}

api.interceptors.request.use(async (request) => {
  if (MUTATING.includes((request.method ?? 'get').toLowerCase())) {
    await ensureCsrf()
  }

  /*
   * X-Socket-ID: по нему сервер не присылает нам событие о нашем же действии
   * (dontBroadcastToCurrentUser в GameService::publish). Свежее состояние
   * приходит прямо в ответе на этот запрос, а событие поверх него заставляло
   * сходить за тем же ещё раз — и второй рендер приходился на анимацию
   * броска.
   */
  const socket = ownSocket()

  if (socket !== undefined) request.headers['X-Socket-ID'] = socket

  return request
})

api.interceptors.response.use(undefined, async (error) => {
  const request = error.config

  // 419 — просроченная сессия: кука ещё есть, но токен в ней уже не тот.
  // Один раз обновляем её и повторяем запрос: иначе игрок теряет ход
  // просто потому, что вкладка провисела ночь.
  if (error.response?.status === 419 && request !== undefined && request.retried !== true) {
    request.retried = true
    await refreshCsrf()

    return api.request(request)
  }

  return Promise.reject(error)
})
